import { useEffect, useMemo, useState } from "react";
import { Alert, Card, Checkbox, DatePicker, Empty, Space, Table, Typography } from "antd";
import type { ColumnsType } from "antd/es/table";
import { useDataProvider, useGetIdentity } from "@refinedev/core";
import { useNavigate } from "react-router";
import dayjs from "dayjs";
import { HealthOverviewAllWindowsResponse } from "./components";

type UserIdentity = {
  isStaff?: boolean;
};

type CombinedConcentratorRow = {
  concentrator_id: number;
  concentrator_name: string;
  windows: Record<
    string,
    {
      degraded_count: number;
      degraded_count_excluding_weekends: number;
      missing_ratio: number;
      missing_excluding_weekends_ratio: number;
    }
  >;
};

export const HealthPage = (): JSX.Element => {
  const dataProvider = useDataProvider()();
  const { data: identity } = useGetIdentity<UserIdentity>();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [snapshotDate, setSnapshotDate] = useState<string | null>(null);
  const [latestAvailableDate, setLatestAvailableDate] = useState<string | null>(null);
  const [ignoreWeekends, setIgnoreWeekends] = useState(true);
  const [windows, setWindows] = useState<number[]>([]);
  const [rows, setRows] = useState<CombinedConcentratorRow[]>([]);

  useEffect(() => {
    let cancelled = false;

    const loadOverview = async () => {
      setLoading(true);
      try {
        const params = new URLSearchParams({
          page_size: "1",
        });
        if (snapshotDate) {
          params.set("date", snapshotDate);
        }
        const { data } = (await dataProvider.custom?.({
          url: `/api/health/overview/?${params.toString()}`,
          method: "get",
        })) || { data: null };
        const payload = data as HealthOverviewAllWindowsResponse | null;

        if (cancelled) {
          return;
        }

        const resolvedLatest = payload?.latest_available_date ?? null;
        const resolvedDate = payload?.snapshot_date ?? null;

        setLatestAvailableDate(resolvedLatest);
        setWindows(payload?.windows ?? []);
        if (!snapshotDate && resolvedDate) {
          setSnapshotDate(resolvedDate);
        }

        const byConcentrator = new Map<number, CombinedConcentratorRow>();
        (payload?.concentrators ?? []).forEach((item) => {
          byConcentrator.set(item.concentrator_id, {
            concentrator_id: item.concentrator_id,
            concentrator_name:
              item.concentrator_name?.trim() || `Concentrator #${item.concentrator_id}`,
            windows: Object.fromEntries(
              Object.entries(item.windows ?? {}).map(([windowKey, value]) => [
                windowKey,
                {
                  degraded_count: value.degraded_count ?? 0,
                  degraded_count_excluding_weekends:
                    value.degraded_count_excluding_weekends ?? 0,
                  missing_ratio: value.missing_ratio ?? 0,
                  missing_excluding_weekends_ratio:
                    value.missing_excluding_weekends_ratio ?? 0,
                },
              ])
            ),
          });
        });

        setRows(
          Array.from(byConcentrator.values()).sort((a, b) =>
            a.concentrator_name.localeCompare(b.concentrator_name)
          )
        );
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    loadOverview();
    return () => {
      cancelled = true;
    };
  }, [dataProvider, snapshotDate]);

  const subtitle = useMemo(() => {
    if (snapshotDate) {
      return `Snapshot date: ${snapshotDate}`;
    }
    if (latestAvailableDate) {
      return `Latest available snapshot: ${latestAvailableDate}`;
    }
    return "Snapshot health for concentrators.";
  }, [snapshotDate, latestAvailableDate]);

  const columns: ColumnsType<CombinedConcentratorRow> = useMemo(
    () => {
      const dynamicColumns: ColumnsType<CombinedConcentratorRow> = [
        {
          title: "Concentrator",
          dataIndex: "concentrator_name",
          key: "concentrator_name",
          width: 260,
          sorter: (a, b) => a.concentrator_name.localeCompare(b.concentrator_name),
          render: (_, row) => row.concentrator_name || `Concentrator #${row.concentrator_id}`,
        },
      ];

      const degradedColumns: ColumnsType<CombinedConcentratorRow> = [];
      const missingColumns: ColumnsType<CombinedConcentratorRow> = [];
      windows.forEach((windowDays) => {
        const key = String(windowDays);
        degradedColumns.push({
          title: `${windowDays}d`,
          key: `degraded_${key}`,
          width: 110,
          render: (_, row) =>
            ignoreWeekends
              ? row.windows[key]?.degraded_count_excluding_weekends ?? 0
              : row.windows[key]?.degraded_count ?? 0,
          sorter: (a, b) => {
            const left = ignoreWeekends
              ? a.windows[key]?.degraded_count_excluding_weekends ?? 0
              : a.windows[key]?.degraded_count ?? 0;
            const right = ignoreWeekends
              ? b.windows[key]?.degraded_count_excluding_weekends ?? 0
              : b.windows[key]?.degraded_count ?? 0;
            return left - right;
          },
        });
        missingColumns.push({
          title: `${windowDays}d`,
          key: `missing_${key}`,
          width: 110,
          render: (_, row) => {
            const ratio = ignoreWeekends
              ? row.windows[key]?.missing_excluding_weekends_ratio ?? 0
              : row.windows[key]?.missing_ratio ?? 0;
            return `${(ratio * 100).toFixed(1)}%`;
          },
          sorter: (a, b) => {
            const left = ignoreWeekends
              ? a.windows[key]?.missing_excluding_weekends_ratio ?? 0
              : a.windows[key]?.missing_ratio ?? 0;
            const right = ignoreWeekends
              ? b.windows[key]?.missing_excluding_weekends_ratio ?? 0
              : b.windows[key]?.missing_ratio ?? 0;
            return left - right;
          },
        });
      });

      if (degradedColumns.length > 0) {
        dynamicColumns.push({
          title: "Degraded",
          key: "degraded_group",
          children: degradedColumns,
        });
      }

      if (missingColumns.length > 0) {
        dynamicColumns.push({
          title: "Missing %",
          key: "missing_group",
          children: missingColumns,
        });
      }

      return dynamicColumns;
    },
    [ignoreWeekends, windows]
  );

  if (identity && !identity.isStaff) {
    return (
      <Alert
        type="error"
        showIcon
        message="Access denied"
        description="System Health is available only for staff users."
      />
    );
  }

  return (
    <Space direction="vertical" style={{ width: "100%" }} size="large">
      <div>
        <Typography.Title level={2} style={{ marginBottom: 0 }}>
          System Health
        </Typography.Title>
        <Typography.Text type="secondary">{subtitle}</Typography.Text>
      </div>

      <Card>
        <Space wrap>
          <Typography.Text strong>Snapshot Date</Typography.Text>
          <DatePicker
            value={snapshotDate ? dayjs(snapshotDate) : null}
            allowClear
            onChange={(value) => setSnapshotDate(value ? value.format("YYYY-MM-DD") : null)}
          />
          <Checkbox
            checked={ignoreWeekends}
            onChange={(event) => setIgnoreWeekends(event.target.checked)}
          >
            Ignore weekends
          </Checkbox>
        </Space>
      </Card>

      {rows.length === 0 && !loading ? (
        <Empty description="No health snapshots yet. Wait for the scheduled daily run." />
      ) : (
        <Card>
          <Table
            rowKey={(row) => row.concentrator_id}
            loading={loading}
            columns={columns}
            dataSource={rows}
            pagination={false}
            scroll={{ x: 280 + windows.length * 220 }}
            onRow={(row) => ({
              onClick: () => {
                const dateQuery = snapshotDate ? `?date=${snapshotDate}` : "";
                navigate(`/health/${row.concentrator_id}${dateQuery}`);
              },
              style: { cursor: "pointer" },
            })}
          />
        </Card>
      )}
    </Space>
  );
};
